import { Button, Icons, useToggle } from '@commenda/kakashi';
import { getServiceRequestServiceId } from '@commenda/types';
import {
  CorporationType,
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { CorporationUploadFileModal } from '../../../common/modals/CorporationUploadFileModal';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { useServiceRequestFile } from '../../../hook/useServiceRequestFile';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { updateTaskStatus, viewUploadedFile } from '../../../utils';

export const IncorpSignedDocumentUploadTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const uploadModalVisibility = useToggle();

  const businessEntityType = serviceRequest.commendaService?.businessEntityType;
  const isLlp = businessEntityType
    ? businessEntityType === CorporationType.LLP
    : getServiceRequestServiceId(serviceRequest) === 'EM_INC_IN_LLP';
  const isPvtLtd = businessEntityType
    ? businessEntityType === CorporationType.PvtLtd
    : getServiceRequestServiceId(serviceRequest) === 'EM_INC_IN_PVTLTD';

  let documentType: LegacyDocumentType;
  if (isLlp) documentType = LegacyDocumentType.IndLLPIncorporationDocument;
  else if (isPvtLtd)
    documentType = LegacyDocumentType.IndPvtIncorporationDocument;

  const [file, setFile] = useServiceRequestFile(serviceRequest, documentType);

  const handleViewFile = () => viewUploadedFile(file.id);

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Client uploads signed document" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button size="sm" variant="secondary" onClick={handleViewFile}>
                View signed document
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Client uploads signed document"
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={uploadModalVisibility.show}
                variant="primary"
              >
                <Icons.Upload01 className="size-4" />
                Upload
              </Button>
            ),
            label: <ServiceRequestTaskLabel label="Upload signed document" />,
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                <Icons.Upload01 className="size-4" />
                Upload
              </Button>
            ),
            label: <ServiceRequestTaskLabel label="Upload signed document" />,
          },
          [TaskStatus.COMPLETED]: {
            actions: file?.id ? (
              <Button size="sm" variant="secondary" onClick={handleViewFile}>
                View upload
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="Signed document uploaded"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{
          role,
          status: task.status,
        }}
        context={context}
        config={config}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />

      <CorporationUploadFileModal
        serviceRequest={serviceRequest}
        serviceRequestTaskId={task.id}
        isOpen={uploadModalVisibility.visible}
        setIsOpen={uploadModalVisibility.hide}
        file={file}
        setFile={setFile}
        heading="Upload Signed Incorporation Document"
        documentType={documentType}
        onUploadSuccess={async () => {
          await updateTaskStatus({
            task,
            status: TaskStatus.COMPLETED,
            mutate,
          });
        }}
      />
    </div>
  );
};
